import React from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";

import Heading from "../common/Heading";
import BreadcrumbList from "../common/BreadcrumbList";
import Overview from "./Overview";
import Gallery from "./Gallery";
import Contact from "./Contact";
import JobActions from "./JobActions";
import DogCard from "../common/DogCard";
import Services from "./Services";
import Sections from "./Sections";
import Map from "./Map";
import OtherCities from "./OtherCities";
import Availability from "./Availability";
import Reviews from "./Reviews";
import HowItWorks from "./HowItWorks";
import Details from "./Details";
import Statistics from "./Statistics";
import { getContactParams } from "../search/SitterCard";
import {
  CaregiverStatus,
  GetCaregiverQuery,
  GetCurrentUserIdQuery
} from "../../generated/graphql";
import { useIsMobile } from "../../lib/media";

export interface JobApplicationDetails {
  jobMatchId: string;
  dogs: any;
  startDate: string;
  endDate: string;
  serviceDates?: Array<string>;
  datesRequested: number;
  sitterId: string;
  sitterName: string;
  serviceTypeId: string;
  paymentTotal: string;
}

interface Props {
  query?: Record<string, any>; // extract query params from URL to pass to contact page
  caregiver: GetCaregiverQuery["caregiver"];
  currentUser: GetCurrentUserIdQuery["currentUser"];
  loading: boolean;
  fetchMore: any;
  showOtherCities?: boolean;
  modalView?: boolean;
  jobApplicationDetails?: JobApplicationDetails;
  onAction?: (conversationId?: string) => void;
}

function SitterProfile({
  query,
  caregiver,
  currentUser,
  loading,
  fetchMore,
  showOtherCities,
  modalView,
  jobApplicationDetails,
  onAction
}: Props) {
  const { t } = useTranslation("sitterprofile");
  const isMobile = useIsMobile();

  // Disable visits service on Caregiver Profile
  const caregiverServices = caregiver?.services?.filter(
    el => el.serviceTypeId !== "9"
  );

  const sections = {
    about: {
      name: "sitterprofile:aboutMe",
      path: "about",
      render: true
    },
    services: {
      name: "sitterprofile:services",
      path: "services",
      render: !!caregiver?.services?.length
    },
    availabilty: {
      name: "sitterprofile:availability",
      path: "availability",
      render: !!caregiver?.services?.length
    },
    location: {
      name: "sitterprofile:location",
      path: "location",
      render: true
    },
    reviews: {
      name: "sitterprofile:reviews",
      path: "reviews",
      render: false
    }
  };

  if (
    caregiver?.caregiverReviewsCount &&
    caregiver?.caregiverReviewsCount > 0
  ) {
    sections.reviews.render = true;
  }

  return (
    <>
      <Box itemType="http://schema.org/Product" itemScope>
        <meta itemProp="mpn" content={caregiver?.id} />
        <meta itemProp="sku" content={caregiver?.id} />

        <Box
          as="div"
          itemProp="brand"
          itemType="http://schema.org/Brand"
          itemScope
        >
          <meta itemProp="name" content={caregiver?.cleanHeadline!} />
        </Box>

        <Flex>
          <Box ml="auto" mr="auto" width={1}>
            <Overview
              slug={caregiver?.slug!}
              avatar={caregiver?.avatar?.profile!}
              title={caregiver?.cleanHeadline!}
              name={caregiver?.name!}
              availablePromotions={caregiver?.availablePromotions!}
              reviewScore={caregiver?.caregiverReviewsScore!}
              reviewCount={caregiver?.caregiverReviewsCount!}
              completedBookingsCount={
                caregiver?.caregiverCompletedBookingsCount!
              }
              repeatCustomersCount={caregiver?.repeatCustomersCount!}
              city={caregiver?.city?.name!}
              address={caregiver?.publicAddress!}
              modalView={modalView}
              isSuperCaregiver={caregiver?.isSuperCaregiver}
            />
          </Box>
        </Flex>

        {!modalView && (
          <Sections
            list={sections}
            currentUser={currentUser}
            caregiver={caregiver}
          />
        )}

        <Flex
          variant={modalView ? undefined : "layout.sitterProfile"}
          width={1}
          mt={[0, 4]}
          ml="auto"
          mr="auto"
        >
          <Box width={[1, 4 / 6]}>
            {caregiver?.photos?.length ? (
              <Gallery photos={caregiver?.photos} imagePriority />
            ) : null}

            <Box pt={4}>
              <Statistics caregiver={caregiver} />
            </Box>

            <Box pt={3}>
              <BreadcrumbList
                breadcrumbs={[
                  {
                    name: t("gudog"),
                    href: "/",
                    position: "1"
                  },
                  {
                    name: t("dogSitters"),
                    href: "/search",
                    position: "2"
                  },
                  {
                    name: t("dogSittersInCity", {
                      city: caregiver?.city?.name!
                    }),
                    href: {
                      pathname: "/search/city",
                      query: {
                        city: caregiver?.city?.slug
                      }
                    },
                    position: "3"
                  }
                ]}
                activeItem={{
                  name: caregiver?.cleanHeadline!,
                  href: "/sitterprofile",
                  position: "4"
                }}
              />
            </Box>

            <Box pt={2} id={sections["about"].path}>
              <Heading
                as="h2"
                lineHeight="heading"
                fontSize={5}
                fontWeight="book"
              >
                <Trans
                  values={{
                    name: caregiver?.name!
                  }}
                  i18nKey="sitterprofile:about"
                />
              </Heading>
              <Text
                mt={2}
                sx={{
                  whiteSpace: "pre-line"
                }}
                itemProp="description"
                content={caregiver?.caregiverDescription}
              >
                {caregiver?.caregiverDescription}
              </Text>
            </Box>

            <Heading as="h3" mt={4} fontSize={2} fontWeight="bold" mb={1}>
              <Trans
                values={{
                  name: caregiver?.name!
                }}
                i18nKey="sitterprofile:userPack"
              />
            </Heading>

            {!caregiver?.dogs?.length && (
              <Text as="p" color="darkGray">
                <Trans
                  i18nKey="sitterprofile:noDogs"
                  values={{ name: caregiver?.name! }}
                />
              </Text>
            )}

            {!!caregiver?.dogs?.length && (
              <Box>
                {caregiver?.dogs.map((dog, index) => (
                  <Box key={index}>
                    <DogCard dog={dog} />
                  </Box>
                ))}
              </Box>
            )}

            {caregiver && (
              <Box>
                <Details caregiver={caregiver} />
              </Box>
            )}

            {!!caregiver?.services?.length && (
              <Services
                id={sections["services"].path}
                services={caregiverServices}
              />
            )}

            {!!caregiver?.services?.length && (
              <>
                <Box id={sections["availabilty"].path}>
                  <Heading as="h2" mt={5} mb={2} fontSize={5} fontWeight="bold">
                    <Trans i18nKey="sitterprofile:availability" />
                  </Heading>
                </Box>

                <Availability
                  caregiver={caregiver}
                  services={caregiver?.services}
                />
              </>
            )}

            {caregiver?.latitude && caregiver?.longitude ? (
              <Box id={sections["location"].path}>
                <Heading as="h2" mt={4} mb={3} fontSize={5} fontWeight="bold">
                  <Trans i18nKey="sitterprofile:location" />
                </Heading>
                <Box mx={[-2, 0]}>
                  <Map lat={caregiver?.latitude} lng={caregiver?.longitude} />
                </Box>
              </Box>
            ) : null}

            {caregiver?.caregiverReviewsCount &&
            caregiver?.caregiverReviewsCount > 0 ? (
              <Box id={sections["reviews"].path}>
                <Reviews
                  {...{ loading, reviews: caregiver?.reviews, fetchMore }}
                  reviewsCount={caregiver?.caregiverReviewsCount!}
                  reviewScore={caregiver?.caregiverReviewsScore!}
                />
              </Box>
            ) : null}
          </Box>

          <Box pl={[0, 3]} width={[0, 2 / 6]}>
            {!!jobApplicationDetails ? (
              <>
                {!isMobile && ( // mobile managed on L321
                  <JobActions
                    jobApplicationDetails={jobApplicationDetails}
                    modalView={modalView}
                    onAction={onAction}
                  />
                )}
              </>
            ) : (
              <Contact
                id={caregiver?.id!}
                title={caregiver?.cleanHeadline!}
                name={caregiver?.name!}
                queryParams={query && getContactParams(query)}
                address={caregiver?.publicAddress!}
                isAvailable={
                  !!caregiver?.services?.length &&
                  caregiver?.caregiverStatus == CaregiverStatus.Active
                }
                modalView={modalView}
              />
            )}
          </Box>
        </Flex>

        <Box
          variant="layout.large"
          mt={5}
          pt={5}
          pb={3}
          sx={{
            borderTop: "default"
          }}
        >
          <HowItWorks />
        </Box>

        {showOtherCities && (
          <Box
            variant="layout.large"
            pb={5}
            sx={{
              borderTop: "default"
            }}
          >
            <OtherCities city={caregiver?.city} />
          </Box>
        )}
      </Box>

      {isMobile && !!jobApplicationDetails && (
        <JobActions
          jobApplicationDetails={jobApplicationDetails}
          modalView={modalView}
          onAction={onAction}
        />
      )}
    </>
  );
}

export default SitterProfile;
